import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Bouton, Etiquette, Liste, Pastille } from '../components/ui/Base'
import { Bandeau, MessageErreur } from '../components/ui/Etats'
import { Squelette } from '../components/ui/Squelette'
import { useToast } from '../components/ui/Toasts'
import { api } from '../lib/api/client'
import { cles } from '../lib/api/cles'
import type { Absence, Disponibilite, Page, Utilisateur } from '../lib/api/types'
import { useSession } from '../lib/auth'
import { formatDate } from '../lib/formatters'
import {
  ajouterJours,
  aujourdhui,
  creneaux,
  JOURS_SEMAINE_COURT,
  lundiDeLaSemaine,
  minutesDepuisMinuit,
  versHeure,
} from '../lib/temps'

const PAS = 30
const OUVERTURE = '06:00'
const FERMETURE = '21:00'

const MOTIFS: Record<Absence['motif'], string> = {
  CONGES: 'Congés',
  MALADIE: 'Maladie',
  FORMATION: 'Formation',
  AUTRE: 'Autre',
}

/**
 * Déclaration des disponibilités.
 *
 * La saisie se fait par **clic-glissé** sur la grille, et par le clavier :
 * `Espace` bascule le créneau sous le curseur. Une grille de 7 × 30 cases
 * qu'on ne peut remplir qu'à la souris est inutilisable pour qui déclare ses
 * disponibilités le soir, sur une tablette.
 */
export function Disponibilites() {
  const [parametres, setParametres] = useSearchParams()
  const { utilisateur, estTitulaire } = useSession()
  const queryClient = useQueryClient()
  const toast = useToast()

  const lundi = lundiDeLaSemaine(parametres.get('lundi') ?? aujourdhui())
  const utilisateurId = parametres.get('utilisateur_id') ?? utilisateur?.id ?? ''

  const [brouillon, setBrouillon] = useState<Map<string, 'DISPONIBLE' | 'INDISPONIBLE'>>(new Map())
  const [modeTrace, setModeTrace] = useState<'DISPONIBLE' | 'INDISPONIBLE'>('DISPONIBLE')
  const [enTrace, setEnTrace] = useState(false)

  const heures = useMemo(() => creneaux(OUVERTURE, FERMETURE, PAS), [])

  const requete = useQuery({
    queryKey: cles.disponibilites.semaine(lundi, utilisateurId),
    queryFn: ({ signal }) =>
      api.get<Page<Disponibilite>>(
        `/disponibilites?debut=${lundi}${utilisateurId ? `&utilisateur_id=${utilisateurId}` : ''}`,
        signal,
      ),
  })

  const requeteAbsences = useQuery({
    queryKey: cles.absences.liste(),
    queryFn: ({ signal }) => api.get<Page<Absence>>('/absences', signal),
  })

  const requeteInfirmiers = useQuery({
    queryKey: cles.utilisateurs.liste(),
    queryFn: ({ signal }) => api.get<Page<Utilisateur>>('/utilisateurs', signal),
    enabled: estTitulaire,
  })

  const enregistrer = useMutation({
    mutationFn: () =>
      api.post('/disponibilites/semaine', {
        utilisateur_id: utilisateurId,
        lundi,
        creneaux: [...brouillon.entries()].map(([cle, type]) => {
          const [date, heure] = cle.split('|')
          return { date, heure_debut: heure, heure_fin: versHeure(minutesDepuisMinuit(heure ?? '00:00') + PAS), type }
        }),
      }),
    onSuccess: () => {
      toast(`${brouillon.size} créneaux enregistrés.`, 'succes')
      setBrouillon(new Map())
      void queryClient.invalidateQueries({ queryKey: cles.disponibilites.tout })
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Enregistrement impossible.', 'erreur'),
  })

  const valider = useMutation({
    mutationFn: ({ type, id }: { type: 'disponibilite' | 'absence'; id: string }) =>
      api.post(type === 'disponibilite' ? `/disponibilites/${id}/valider` : `/absences/${id}/valider`),
    onSuccess: () => {
      toast('Validé.', 'succes')
      void queryClient.invalidateQueries({ queryKey: cles.disponibilites.tout })
      void queryClient.invalidateQueries({ queryKey: cles.absences.tout })
    },
  })

  /** Ce que la base connaît, indexé pour un accès direct par case. */
  const existantes = useMemo(() => {
    const carte = new Map<string, Disponibilite>()
    ;(requete.data?.items ?? []).forEach((d) => {
      const debut = minutesDepuisMinuit(d.heure_debut)
      const fin = minutesDepuisMinuit(d.heure_fin)
      for (let m = debut; m < fin; m += PAS) carte.set(`${d.date}|${versHeure(m)}`, d)
    })
    return carte
  }, [requete.data])

  function basculer(cle: string) {
    setBrouillon((precedent) => {
      const suivant = new Map(precedent)
      if (suivant.get(cle) === modeTrace) suivant.delete(cle)
      else suivant.set(cle, modeTrace)
      return suivant
    })
  }

  function changer(cle: string, valeur: string) {
    const suivants = new URLSearchParams(parametres)
    if (valeur) suivants.set(cle, valeur)
    else suivants.delete(cle)
    setParametres(suivants, { replace: true })
  }

  return (
    <div className="space-y-4" onMouseUp={() => setEnTrace(false)} onMouseLeave={() => setEnTrace(false)}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Disponibilités</h1>
          <p className="text-sm text-slate-600">
            Semaine du {formatDate(lundi)} au {formatDate(ajouterJours(lundi, 6))}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {estTitulaire ? (
            <Liste
              aria-label="Infirmier"
              value={utilisateurId}
              onChange={(e) => changer('utilisateur_id', e.target.value)}
            >
              {(requeteInfirmiers.data?.items ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.prenom} {u.nom}
                </option>
              ))}
            </Liste>
          ) : null}
          <Bouton variante="secondaire" onClick={() => changer('lundi', ajouterJours(lundi, -7))}>←</Bouton>
          <Bouton variante="secondaire" onClick={() => changer('lundi', aujourdhui())}>Cette semaine</Bouton>
          <Bouton variante="secondaire" onClick={() => changer('lundi', ajouterJours(lundi, 7))}>→</Bouton>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-3">
        <span className="text-sm font-medium text-slate-700">Tracer :</span>
        {(['DISPONIBLE', 'INDISPONIBLE'] as const).map((type) => (
          <button
            key={type}
            type="button"
            aria-pressed={modeTrace === type}
            onClick={() => setModeTrace(type)}
            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
              modeTrace === type ? 'border-sky-500 bg-sky-50 text-sky-800' : 'border-slate-300 text-slate-600'
            }`}
          >
            <Pastille couleur={type === 'DISPONIBLE' ? '#10b981' : '#f87171'} />
            {type === 'DISPONIBLE' ? 'Disponible' : 'Indisponible'}
          </button>
        ))}
        {brouillon.size > 0 ? (
          <>
            <Etiquette ton="info">{brouillon.size} créneaux modifiés</Etiquette>
            <Bouton onClick={() => enregistrer.mutate()} disabled={enregistrer.isPending}>
              {enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Bouton>
            <Bouton variante="discret" onClick={() => setBrouillon(new Map())}>Annuler</Bouton>
          </>
        ) : (
          <span className="text-xs text-slate-500">
            Cliquez-glissez sur la grille, ou naviguez au clavier et appuyez sur Espace.
          </span>
        )}
      </div>

      {requete.isPending ? (
        <Squelette lignes={8} />
      ) : requete.isError ? (
        <MessageErreur erreur={requete.error} onReessayer={() => requete.refetch()} />
      ) : (
        <>
          {existantes.size === 0 && brouillon.size === 0 ? (
            <Bandeau ton="attention">
              Aucune disponibilité déclarée pour cette semaine. Le planning reste consultable, mais
              le calcul de couverture considérera l&apos;infirmier comme absent.
            </Bandeau>
          ) : null}

          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <div
              className="grid min-w-[48rem]"
              style={{
                gridTemplateColumns: '4rem repeat(7, minmax(5rem, 1fr))',
                gridTemplateRows: `auto repeat(${heures.length}, 1.25rem)`,
              }}
            >
              <div className="border-b border-r border-slate-200 bg-slate-50" />
              {JOURS_SEMAINE_COURT.map((jour, i) => (
                <div key={jour} className="border-b border-r border-slate-200 bg-slate-50 py-1.5 text-center text-xs font-semibold text-slate-600">
                  {jour} {formatDate(ajouterJours(lundi, i)).slice(0, 5)}
                </div>
              ))}

              {heures.map((heure, ligne) => (
                <div
                  key={`h-${heure}`}
                  className="border-b border-r border-slate-200 pr-1 text-right text-[10px] tabular-nums text-slate-400"
                  style={{ gridColumn: 1, gridRow: ligne + 2 }}
                >
                  {heure.endsWith(':00') ? heure : ''}
                </div>
              ))}

              {JOURS_SEMAINE_COURT.map((_, indexJour) =>
                heures.map((heure, ligne) => {
                  const date = ajouterJours(lundi, indexJour)
                  const cle = `${date}|${heure}`
                  const modifie = brouillon.get(cle)
                  const existante = existantes.get(cle)
                  const type = modifie ?? existante?.type
                  return (
                    <button
                      key={cle}
                      type="button"
                      aria-label={`${date} ${heure} — ${type ?? 'non déclaré'}`}
                      aria-pressed={Boolean(type)}
                      onMouseDown={() => {
                        setEnTrace(true)
                        basculer(cle)
                      }}
                      onMouseEnter={() => {
                        if (enTrace) basculer(cle)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault()
                          basculer(cle)
                        }
                      }}
                      className={`border-b border-r focus:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 ${
                        type === 'DISPONIBLE'
                          ? 'border-emerald-200 bg-emerald-200/70'
                          : type === 'INDISPONIBLE'
                            ? 'border-red-200 bg-red-200/70'
                            : 'border-slate-100 hover:bg-slate-100'
                      } ${modifie ? 'ring-1 ring-inset ring-sky-500' : ''}`}
                      style={{ gridColumn: indexJour + 2, gridRow: ligne + 2 }}
                    />
                  )
                }),
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Absences ── */}
      <section className="space-y-2">
        <h2 className="text-lg font-semibold text-slate-900">Absences</h2>
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <caption className="sr-only">Demandes d&apos;absence</caption>
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th scope="col" className="px-4 py-2.5">Période</th>
                <th scope="col" className="px-4 py-2.5">Motif</th>
                <th scope="col" className="px-4 py-2.5">Commentaire</th>
                <th scope="col" className="px-4 py-2.5">Statut</th>
                {estTitulaire ? <th scope="col" className="px-4 py-2.5 text-right">Action</th> : null}
              </tr>
            </thead>
            <tbody>
              {(requeteAbsences.data?.items ?? []).map((absence) => (
                <tr key={absence.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2.5 tabular-nums text-slate-700">
                    {formatDate(absence.date_debut)} – {formatDate(absence.date_fin)}
                  </td>
                  <td className="px-4 py-2.5 text-slate-700">{MOTIFS[absence.motif]}</td>
                  <td className="px-4 py-2.5 text-slate-600">{absence.commentaire ?? '—'}</td>
                  <td className="px-4 py-2.5">
                    <Etiquette
                      ton={
                        absence.statut === 'VALIDEE' ? 'succes' : absence.statut === 'REFUSEE' ? 'danger' : 'attention'
                      }
                    >
                      {absence.statut}
                    </Etiquette>
                  </td>
                  {estTitulaire ? (
                    <td className="px-4 py-2.5 text-right">
                      {absence.statut === 'DEMANDEE' ? (
                        <Bouton
                          variante="secondaire"
                          onClick={() => valider.mutate({ type: 'absence', id: absence.id })}
                          disabled={valider.isPending}
                        >
                          Valider
                        </Bouton>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
